import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

export interface BrandRow {
  id: string;
  name: string;
  /** Rubro declarado para ESTA marca (`brand_profiles.rubro`). */
  rubro: string | null;
  logo_url: string | null;
  is_default: boolean;
}

export interface BrandProfile {
  id: string;
  name: string | null;
  id_company: string | null;
  company_name: string | null;
  company_logo: string | null;
  /**
   * Rubro a nivel empresa (`company_profiles.rubro`). Es el fallback cuando la
   * marca activa no tiene el suyo — el mismo orden que usa el editor de marca
   * (`config_sections/CompanyData`), para que no haya dos rubros distintos según
   * qué pantalla se mire.
   */
  rubro: string | null;
  plan: string | null;
  imagenes_restantes: number | null;
  max_fotos_mes: number | null;
}

/**
 * Carga el perfil del usuario + sus créditos + sus marcas (brand_profiles) y
 * permite cambiar de marca activa. Espeja la lógica del Sidebar pero como hook
 * reutilizable. Se mantiene en sync con el resto de la app vía los eventos
 * `atlas:brand-changed` y `atlas:credits-updated`.
 */
export function useBrandProfile() {
  const [profile, setProfile] = useState<BrandProfile | null>(null);
  const [brands, setBrands] = useState<BrandRow[]>([]);
  const [currentBrandId, setCurrentBrandId] = useState<string | null>(null);
  const [switching, setSwitching] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      const { data: prof } = await supabase
        .from("profiles")
        .select("id, name, id_company")
        .eq("id", user.id)
        .single();
      if (!prof) return;
      if (prof.id_company) {
        const { data: company } = await supabase
          .from("company_profiles")
          .select("nombre, logo_url, rubro, plan, max_fotos_mes, imagenes_restantes")
          .eq("id_company", prof.id_company)
          .single();
        setProfile({
          id: prof.id,
          name: prof.name,
          id_company: prof.id_company,
          company_name: company?.nombre ?? null,
          company_logo: company?.logo_url ?? null,
          rubro: company?.rubro ?? null,
          plan: company?.plan ?? null,
          imagenes_restantes: company?.imagenes_restantes ?? null,
          max_fotos_mes: company?.max_fotos_mes ?? null,
        });
      } else {
        setProfile({
          id: prof.id,
          name: prof.name,
          id_company: null,
          company_name: null,
          company_logo: null,
          rubro: null,
          plan: null,
          imagenes_restantes: null,
          max_fotos_mes: null,
        });
      }
    } catch {
      /* noop */
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const fetchBrands = useCallback(async (companyId: string) => {
    try {
      const { data: brandsData } = await supabase
        .from("brand_profiles")
        .select("id, name, rubro, logo_url, is_default")
        .eq("id_company", companyId)
        .eq("is_draft", false)
        .order("is_default", { ascending: false })
        .order("created_at", { ascending: true });
      const list = (brandsData as BrandRow[] | null) ?? [];
      setBrands(list);

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profData } = await supabase
        .from("profiles")
        .select("current_brand_id")
        .eq("id", user.id)
        .single();
      const fromProfile = (profData as { current_brand_id?: string | null })
        ?.current_brand_id as string | null;
      const fallback =
        list.find((b) => b.is_default)?.id ?? list[0]?.id ?? null;
      setCurrentBrandId(fromProfile ?? fallback);
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (profile?.id_company) fetchBrands(profile.id_company);
  }, [profile?.id_company, fetchBrands]);

  // Sync con el resto de la app.
  useEffect(() => {
    const onBrand = () => {
      if (profile?.id_company) fetchBrands(profile.id_company);
      // También el perfil de empresa, y no sólo las marcas — ADR-2026-07-31-editar-nombre-rubro-marca-refleja. El evento ya
      // no significa únicamente "cambió la marca activa": desde que las ediciones de
      // nombre y rubro lo emiten, puede haber cambiado `company_profiles`, que es de
      // donde salen los fallbacks (`company_name`, `rubro`) y el único lugar que se
      // escribe cuando la cuenta no tiene marcas. Va en `silent` para no encender el
      // `loading` y hacer parpadear a los consumidores.
      fetchProfile(true);
    };
    const onCredits = () => fetchProfile(true);
    window.addEventListener("atlas:brand-changed", onBrand);
    window.addEventListener("atlas:credits-updated", onCredits);
    return () => {
      window.removeEventListener("atlas:brand-changed", onBrand);
      window.removeEventListener("atlas:credits-updated", onCredits);
    };
  }, [profile?.id_company, fetchBrands, fetchProfile]);

  const switchBrand = useCallback(
    async (brandId: string) => {
      if (brandId === currentBrandId || switching) return;
      setSwitching(true);
      const prev = currentBrandId;
      setCurrentBrandId(brandId); // optimista
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error("no user");
        const { error } = await supabase
          .from("profiles")
          .update({ current_brand_id: brandId })
          .eq("id", user.id);
        if (error) throw error;
        window.dispatchEvent(
          new CustomEvent("atlas:brand-changed", { detail: { brandId } }),
        );
      } catch {
        setCurrentBrandId(prev); // rollback
      } finally {
        setSwitching(false);
      }
    },
    [currentBrandId, switching],
  );

  const activeBrand =
    brands.find((b) => b.id === currentBrandId) ??
    brands.find((b) => b.is_default) ??
    brands[0] ??
    null;

  return {
    profile,
    brands,
    currentBrandId,
    activeBrand,
    switchBrand,
    switching,
    loading,
    refresh: fetchProfile,
  };
}
